import { detectEmailKey } from "./winnerSelectCsvValidation";

const PDF_PAGE_WIDTH = 612;
const PDF_PAGE_HEIGHT = 792;
const PDF_MARGIN_X = 54;
const PDF_START_Y = 738;
const PDF_FONT_SIZE = 11;
const PDF_HEADER_FONT_SIZE = 15;
const PDF_LINE_HEIGHT = 14;
const PDF_MAX_LINE_CHARS = 88;
const PDF_MAX_LINES_PER_PAGE = 46;
const REPORT_PERCENT_DECIMALS = 4;
const OMITTED_WINNER_RECORD_KEYS = new Set([
  "amoecreatedate",
  "creationdate",
  "lastupdated",
  "orderid",
  "entrysource",
]);

export function downloadWinnerReportPdf({ payload, winner, drawnAt }) {
  const lines = buildWinnerReportLines({ payload, winner, drawnAt });
  const pdfText = buildPdfDocument(lines);
  const fileName = buildReportFileName(payload, drawnAt);
  const blob = new Blob([pdfText], { type: "application/pdf" });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function buildWinnerReportLines({ payload, winner, drawnAt }) {
  const aggregated = Array.isArray(payload?.aggregated) ? payload.aggregated : [];
  const totalEntries = aggregated.reduce(
    (sum, row) => sum + Math.max(0, toNumber(row?.[payload?.entriesKey])),
    0
  );
  const reportAnalytics = payload?.reportAnalytics || {};
  const exportMetadata = payload?.exportMetadata || {};
  const winnerEmail = findWinnerEmail(winner?.row, payload?.emailKey);
  const winnerAnalytics = buildWinnerAnalytics({
    winnerEmail,
    winnerEntries: winner?.entries,
    totalEntries,
    aggregatedCount: aggregated.length,
    reportAnalytics,
  });
  const lines = [];

  lines.push("Clean Sweeps Winner Selection Report");
  lines.push("");

  pushSection(lines, "Winner Selection Report", [
    ["Generated", formatDateTime(Date.now())],
    ["Drawing date/time", formatDateTime(drawnAt)],
    ["Uploaded file", safeDisplay(payload?.fileName)],
    ["File upload date/time", formatDateTime(payload?.fileUploadedAt)],
  ]);

  pushSection(lines, "Sweepstakes Details", [
    ["Sweepstakes ID", safeDisplay(exportMetadata.sweepstakesId)],
    ["Sweepstakes name", safeDisplay(exportMetadata.sweepstakesName)],
    ["Sweepstakes start date", safeDisplay(exportMetadata.sweepstakesStartDate)],
    ["Sweepstakes end date", safeDisplay(exportMetadata.sweepstakesEndDate)],
  ]);

  pushSection(lines, "Pool Summary", [
    ["Total pool entries", formatInteger(totalEntries)],
    ["Unique entrants", formatInteger(aggregated.length)],
    ["Winner entries", formatInteger(winner?.entries)],
    ["Winner share", `${safeDisplay(winner?.share || "0.0000")}%`],
  ]);

  pushSection(lines, "Winner Details", [
    ["Display name", safeDisplay(winner?.name)],
    ["First name", safeDisplay(winner?.firstLast?.first)],
    ["Last name", safeDisplay(winner?.firstLast?.last)],
    ["Winner email", safeDisplay(winnerEmail)],
  ]);

  lines.push("WINNER RECORD");
  appendWinnerRecordLines(lines, winner?.row);
  lines.push("");

  pushSection(lines, "Winner Analytics", [
    ["Winner entrant key", safeDisplay(winnerEmail)],
    ["Total pool entries", formatInteger(totalEntries)],
    ["Total raw rows", formatInteger(reportAnalytics.rawRowsSeen)],
    ["Rows after merging same customer entry rows", formatInteger(winnerAnalytics.rowsAfterCombiningCustomer)],
    ["Rows with usable data", formatInteger(reportAnalytics.dataRows)],
    ["Rows counted in pool", formatInteger(reportAnalytics.poolRows)],
    ["Rows ignored", formatInteger(reportAnalytics.ignoredRows)],
    ["Ignored empty rows", formatInteger(reportAnalytics.emptyRowsIgnored)],
    ["Ignored blank-email rows", formatInteger(reportAnalytics.blankEmailRowsIgnored)],
    ["Zero-entry rows", formatInteger(reportAnalytics.zeroEntryRows)],
    ["Winner exact-email raw rows", formatInteger(winnerAnalytics.exactRows)],
    ["Winner exact-email pooled rows", formatInteger(winnerAnalytics.exactPoolRows)],
    ["Winner total combined entries", formatInteger(winnerAnalytics.familyEntries)],
    ["Winner total combined rows", formatInteger(winnerAnalytics.familyRows)],
    ["Winner total share percentage", formatPercent(winnerAnalytics.familyEntries, totalEntries)],
  ]);

  lines.push("BLANK EMAIL-COLUMN ROWS");
  lines.push(
    "  These rows were ignored because the email column was blank. Any non-empty value in that column is treated as the entrant key and included in the entrant pool."
  );
  appendBlankEmailRowLines(lines, reportAnalytics.blankEmailRows);
  lines.push("");

  lines.push("RELATED WINNER VARIANTS");
  lines.push(
    "  These are alternate winner email values that match the same winner after trimming leading or trailing commas and semicolons, but are still treated as separate entrant keys in the current parser."
  );
  appendVariantLines(lines, winnerAnalytics.variants);

  return lines;
}

function appendWinnerRecordLines(lines, row) {
  const keys = Object.keys(row || {}).filter(
    (key) => !OMITTED_WINNER_RECORD_KEYS.has(normalizeLabel(key))
  );

  if (!keys.length) {
    lines.push("  No winner row data available.");
    return;
  }

  keys.forEach((key) => {
    lines.push(`  ${String(key)}: ${safeDisplay(row[key])}`);
  });
}

function appendVariantLines(lines, variants) {
  const list = Array.isArray(variants) ? variants : [];

  if (!list.length) {
    lines.push("  No related variants found.");
    return;
  }

  list.forEach((variant) => {
    lines.push(
      `  ${safeDisplay(variant?.key)} | entries: ${formatInteger(variant?.entries)} | rows: ${formatInteger(variant?.rows)}`
    );
  });
}

function appendBlankEmailRowLines(lines, blankEmailRows) {
  const list = Array.isArray(blankEmailRows) ? blankEmailRows : [];

  if (!list.length) {
    lines.push("  No blank-email rows were ignored.");
    return;
  }

  list.forEach((blankRow) => {
    const rowNumber = Math.max(0, Number(blankRow?.rowNumber) || 0);
    const rowLabel = rowNumber ? `Row ${rowNumber}` : "Row";
    lines.push(
      `  ${rowLabel} | name: ${safeDisplay(blankRow?.name)} | entries: ${formatInteger(
        blankRow?.entries
      )}`
    );
  });
}

function pushSection(lines, title, fields) {
  lines.push(title.toUpperCase());
  (fields || []).forEach(([label, value]) => {
    lines.push(`  ${label}: ${value}`);
  });
  lines.push("");
}

function buildWinnerAnalytics({
  winnerEmail,
  winnerEntries,
  totalEntries,
  aggregatedCount,
  reportAnalytics,
}) {
  const rowStatsByEmail =
    reportAnalytics?.rowStatsByEmail && typeof reportAnalytics.rowStatsByEmail === "object"
      ? reportAnalytics.rowStatsByEmail
      : {};
  const exactStats =
    (winnerEmail && rowStatsByEmail[winnerEmail]) || {
      entries: Math.max(0, Number(winnerEntries) || 0),
      rows: 0,
    };
  const familyKey = normalizeAnalyticsFamilyKey(winnerEmail);
  let familyEntries = 0;
  let familyRows = 0;
  const variants = [];

  Object.entries(rowStatsByEmail).forEach(([emailKey, stats]) => {
    if (!familyKey || normalizeAnalyticsFamilyKey(emailKey) !== familyKey) return;

    const entries = Math.max(0, Number(stats?.entries) || 0);
    const rows = Math.max(0, Number(stats?.rows) || 0);
    familyEntries += entries;
    familyRows += rows;

    if (emailKey !== winnerEmail) {
      variants.push({
        key: emailKey,
        entries,
        rows,
      });
    }
  });

  variants.sort((a, b) => b.entries - a.entries);

  return {
    rowsAfterCombiningCustomer: Math.max(0, Number(aggregatedCount) || 0),
    exactEntries: Math.max(0, Number(exactStats?.entries) || 0),
    exactRows: Math.max(0, Number(exactStats?.rows) || 0),
    exactPoolRows: Math.max(0, Number(exactStats?.rows) || 0),
    familyEntries: familyEntries || Math.max(0, Number(exactStats?.entries) || 0),
    familyRows: familyRows || Math.max(0, Number(exactStats?.rows) || 0),
    totalEntries: Math.max(0, Number(totalEntries) || 0),
    variants,
  };
}

function buildReportFileName(payload, drawnAt) {
  const exportMetadata = payload?.exportMetadata || {};
  const slugBase =
    exportMetadata.sweepstakesId ||
    exportMetadata.sweepstakesName ||
    payload?.fileName ||
    "winner-report";

  const slug = String(slugBase)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "winner-report";

  return `${slug}-winner-report-${formatCompactTimestamp(drawnAt)}.pdf`;
}

function buildPdfDocument(lines) {
  const pages = paginateLines(lines);
  const objects = {};
  const pageIds = [];

  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>";

  let nextId = 5;

  pages.forEach((pageLines, pageIndex) => {
    const contentId = nextId++;
    const pageId = nextId++;
    const contentStream = buildPageContentStream(pageLines, pageIndex);

    objects[contentId] =
      `<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`;
    objects[pageId] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentId} 0 R >>`;
    pageIds.push(pageId);
  });

  objects[2] = `<< /Type /Pages /Count ${pageIds.length} /Kids [${pageIds
    .map((id) => `${id} 0 R`)
    .join(" ")}] >>`;

  const maxId = nextId - 1;
  let pdf = "%PDF-1.4\n";
  const offsets = new Array(maxId + 1).fill(0);

  for (let id = 1; id <= maxId; id++) {
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${maxId + 1}\n`;
  pdf += "0000000000 65535 f \n";

  for (let id = 1; id <= maxId; id++) {
    pdf += `${String(offsets[id]).padStart(10, "0")} 00000 n \n`;
  }

  pdf += `trailer\n<< /Size ${maxId + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
}

function paginateLines(lines) {
  const pages = [];
  let currentPage = [];

  (lines || []).forEach((line) => {
    const wrappedLines = wrapLine(line);

    wrappedLines.forEach((wrappedLine) => {
      if (currentPage.length >= PDF_MAX_LINES_PER_PAGE) {
        pages.push(currentPage);
        currentPage = [];
      }

      currentPage.push(toPdfAscii(wrappedLine));
    });
  });

  if (!currentPage.length) currentPage.push("");
  if (!pages.length || currentPage.length) pages.push(currentPage);
  return pages;
}

function wrapLine(line) {
  const text = String(line ?? "");
  if (!text) return [""];

  const indentation = text.match(/^\s*/)?.[0] || "";
  const content = text.trim();
  if (!content) return [""];

  const words = content.split(/\s+/);
  const result = [];
  let current = indentation;

  words.forEach((word) => {
    const candidate = current.trim()
      ? `${current}${current.trim() ? " " : ""}${word}`.replace(
          new RegExp(`^${escapeRegExp(indentation)}\\s+`),
          indentation
        )
      : `${indentation}${word}`;

    if (candidate.length <= PDF_MAX_LINE_CHARS || current.trim() === "") {
      current = candidate;
      return;
    }

    result.push(current);
    current = `${indentation}${word}`;
  });

  if (current) result.push(current);
  return result.length ? result : [indentation];
}

function buildPageContentStream(lines, pageIndex) {
  let content = "BT\n";
  content += `/F1 ${PDF_FONT_SIZE} Tf\n`;
  content += `${PDF_LINE_HEIGHT} TL\n`;
  content += `${PDF_MARGIN_X} ${PDF_START_Y} Td\n`;

  (lines || []).forEach((line, index) => {
    if (index > 0) content += "T*\n";
    if (pageIndex === 0 && index === 0) {
      content += `/F2 ${PDF_HEADER_FONT_SIZE} Tf\n`;
      content += `(${escapePdfString(line)}) Tj\n`;
      content += `/F1 ${PDF_FONT_SIZE} Tf\n`;
      return;
    }
    content += `(${escapePdfString(line)}) Tj\n`;
  });

  content += "ET";
  return content;
}

function escapePdfString(value) {
  return String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function toPdfAscii(value) {
  return String(value || "").replace(/[^\x20-\x7E]/g, "?");
}

function safeDisplay(value) {
  const text = String(value || "").trim();
  return text || "Not available";
}

function findWinnerEmail(row, payloadEmailKey) {
  const emailKey = payloadEmailKey || detectEmailKey(Object.keys(row || {}));
  if (!emailKey) return "";

  return String(row?.[emailKey] || "").trim().toLowerCase();
}

function formatDateTime(value) {
  const timestamp = Number(value || 0);
  if (!timestamp) return "Not available";

  const formatted = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
  }).format(new Date(timestamp));

  return `${formatted} UTC`;
}

function formatCompactTimestamp(value) {
  const date = value ? new Date(value) : new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const second = String(date.getSeconds()).padStart(2, "0");
  return `${year}${month}${day}${hour}${minute}${second}`;
}

function formatInteger(value) {
  return new Intl.NumberFormat().format(Math.max(0, Number(value) || 0));
}

function formatPercent(value, total) {
  if (!total) return `0.${"0".repeat(REPORT_PERCENT_DECIMALS)}%`;

  return `${((Math.max(0, Number(value) || 0) / total) * 100).toFixed(
    REPORT_PERCENT_DECIMALS
  )}%`;
}

function toNumber(value) {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return 0;
  const parsed = Number(value.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeAnalyticsFamilyKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^[,;]+|[,;]+$/g, "");
}

function normalizeLabel(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}
